import React from 'react'
import { Link } from 'react-router-dom'
import styled from 'styled-components/macro'
import { H3 } from 'src/branding/typography'
import {
  FOOD_SAFETY_SECONDARY_TEXT,
  FOOD_SAFETY_SURFACE,
} from 'src/constants/foodSafety'
import {
  canActionReportException,
  formatReportCorrectiveActionLine,
  formatReportExceptionDay,
  formatReportExceptionHeadline,
  formatReportExceptionKind,
  formatReportExceptionName,
  formatReportExceptionRaised,
  formatReportExceptionSection,
  formatReportExceptionStatus,
  formatEarlierOpenExceptionsLine,
  formatReportExceptionsSummary,
  formatReportRecheckLine,
  isReportExceptionOpen,
  showReportLegacyNote,
  sortReportExceptionsUnresolvedFirst,
} from 'src/utils/foodSafetyReportExceptions'
import { foodSafetyReportsSectionHref } from 'src/utils/foodSafetyReports'

/**
 * Under the Reports grid: every exception this week, what was done, and what
 * is still open. Original readings are shown as recorded.
 */
const FoodSafetyReportExceptions = ({
  venueId,
  exceptions,
  summary,
  earlierOpen = [],
}) => {
  const summaryLine = formatReportExceptionsSummary(summary, exceptions)
  const earlier = Array.isArray(earlierOpen) ? earlierOpen : []
  const earlierLine = formatEarlierOpenExceptionsLine(earlier)
  return (
    <Section
      data-testid='food-safety-report-exceptions'
      aria-labelledby='fs-report-exceptions-title'
    >
      <H3 id='fs-report-exceptions-title'>
        Exceptions &amp; corrective actions
      </H3>
      {exceptions.length === 0 ? (
        <Empty data-testid='food-safety-report-exceptions-empty'>
          {earlierLine
            ? `No exceptions this week. ${earlierLine}`
            : 'No exceptions this week.'}
        </Empty>
      ) : (
        <>
          {summaryLine ? (
            <Summary data-testid='food-safety-report-exceptions-summary'>
              {summaryLine}
            </Summary>
          ) : null}
          <List>
            {sortReportExceptionsUnresolvedFirst(exceptions).map(exception => (
              <ReportExceptionEntry
                key={exception._id}
                venueId={venueId}
                exception={exception}
              />
            ))}
          </List>
        </>
      )}
      {earlier.length ? (
        <>
          {exceptions.length ? (
            <EarlierLine data-testid='food-safety-report-earlier-open'>
              {earlierLine}
            </EarlierLine>
          ) : null}
          <List data-testid='food-safety-report-earlier-open-list'>
            {earlier.map(exception => (
              <ReportExceptionEntry
                key={exception._id}
                venueId={venueId}
                exception={exception}
              />
            ))}
          </List>
        </>
      ) : null}
    </Section>
  )
}

const ReportExceptionEntry = ({ venueId, exception }) => {
  const open = isReportExceptionOpen(exception)
  const kind = formatReportExceptionKind(exception)
  const raised = formatReportExceptionRaised(exception)
  const recheck = formatReportRecheckLine(exception.recheck)
  const href = canActionReportException(exception)
    ? foodSafetyReportsSectionHref(
        venueId,
        exception.section,
        exception.localDate,
      )
    : null
  return (
    <Entry
      data-open={open ? 'true' : 'false'}
      data-testid={`food-safety-report-exception-${exception._id}`}
    >
      <EntryHead>
        <EntryWhere>
          <strong>{formatReportExceptionName(exception)}</strong>
          <Muted>
            {[
              formatReportExceptionDay(exception.localDate),
              formatReportExceptionSection(exception),
            ]
              .filter(Boolean)
              .join(' · ')}
          </Muted>
        </EntryWhere>
        <StatusPill data-open={open ? 'true' : 'false'}>
          {formatReportExceptionStatus(exception)}
        </StatusPill>
      </EntryHead>
      <Headline>
        {formatReportExceptionHeadline(exception)}
        {kind ? <Muted> · {kind}</Muted> : null}
      </Headline>
      {raised ? <Muted>{raised}</Muted> : null}
      {(exception.correctiveActions || []).map((action, i) => (
        <ActionLine
          key={`${exception._id}-action-${i}`}
          data-testid={`food-safety-report-exception-action-${exception._id}`}
        >
          {formatReportCorrectiveActionLine(action)}
        </ActionLine>
      ))}
      {recheck ? (
        <ActionLine
          data-testid={`food-safety-report-exception-recheck-${exception._id}`}
        >
          {recheck}
        </ActionLine>
      ) : null}
      {showReportLegacyNote(exception) ? (
        <Muted
          data-testid={`food-safety-report-exception-legacy-${exception._id}`}
        >
          Flagged from a reading saved before corrective actions — nothing to
          record.
        </Muted>
      ) : null}
      {href ? (
        <RecordLink
          to={href}
          data-testid={`food-safety-report-exception-record-${exception._id}`}
        >
          Record corrective action
        </RecordLink>
      ) : null}
    </Entry>
  )
}

export default FoodSafetyReportExceptions

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: ${({ theme }) => theme.spacing.md};
  padding-top: ${({ theme }) => theme.spacing.sm};
  border-top: 1px solid ${FOOD_SAFETY_SURFACE.HERO_BORDER};

  h3 {
    margin: 0;
  }
`

const Summary = styled.p`
  margin: 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Empty = styled.p`
  margin: 0;
  font-size: 0.9rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const EarlierLine = styled.p`
  margin: 0.4rem 0 0;
  font-size: 0.85rem;
  font-weight: 600;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`

const Entry = styled.li`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  padding: 0.6rem 0.75rem;
  border: 1px solid ${({ theme }) => theme.colours.panSilver || '#eee'};
  border-left: 3px solid ${FOOD_SAFETY_SURFACE.STATUS_DONE};
  border-radius: ${FOOD_SAFETY_SURFACE.CONTROL_RADIUS};

  &[data-open='true'] {
    border-left-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fffaf2;
  }
`

const EntryHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 0.75rem;
`

const EntryWhere = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 0.15rem 0.5rem;
  min-width: 0;
`

const Muted = styled.span`
  font-size: 0.8rem;
  color: ${FOOD_SAFETY_SECONDARY_TEXT};
`

const Headline = styled.span`
  font-size: 0.9rem;
`

const ActionLine = styled.span`
  font-size: 0.85rem;
  padding-left: 0.6rem;
  border-left: 2px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
`

const StatusPill = styled.span`
  flex-shrink: 0;
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.1rem 0.5rem;
  border-radius: 999px;
  border: 1px solid ${FOOD_SAFETY_SURFACE.LIME_EDGE};
  background: ${FOOD_SAFETY_SURFACE.LIME_WASH};
  color: #135200;

  &[data-open='true'] {
    border-color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
    background: #fff2e8;
    color: ${FOOD_SAFETY_SURFACE.STATUS_ATTENTION};
  }
`

const RecordLink = styled(Link)`
  align-self: flex-start;
  font-size: 0.85rem;
  font-weight: 600;
`
